/**
 * Dataset contracts — slice 1, "contacts in".
 *
 * The import is a three-step conversation, and these schemas are the wire shape
 * of each step:
 *
 *   1. **upload-init** — the file is posted, stored behind the `FileStorage`
 *      port, and a `datasets` row is created with `status = 'pending'`. The
 *      first rows are sampled to detect columns, and the response carries both
 *      the detected columns and a *suggested* mapping. Nothing is imported yet.
 *   2. **mapping** — the user corrects the suggestion and confirms it. The
 *      mapping is stored on the row, the import job is enqueued and the status
 *      moves to `processing`.
 *   3. **status / read** — the UI polls until `ready` or `failed`, then shows
 *      the counts and the rejected-row report.
 *
 * Two rules the whole slice is built on:
 *
 * - **Nothing is silently dropped.** A source column that is not mapped onto
 *   `name` / `email` / `phone` is preserved verbatim in `contacts.extras`, and a
 *   row that cannot become a contact appears in the rejected-row report with a
 *   reason — never discarded.
 * - **A contact must be reachable.** At least one of email or phone has to be
 *   mapped, and a row carrying neither is rejected rather than imported. The
 *   database enforces the same thing with the `contacts_reachable` CHECK.
 */
import { z } from 'zod';

import {
  isoDateTimeSchema,
  paginated,
  paginationQuerySchema,
  sortDirectionSchema,
  uuidSchema,
} from '../common.js';
import { datasetStatusSchema } from '../enums.js';

/* -------------------------------------------------------------------------- */
/* Limits                                                                      */
/* -------------------------------------------------------------------------- */

/** 20 MB per upload. Enforced by the API before the file reaches the parser. */
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

/** 200k data rows per file. Rows beyond it are rejected, not truncated away. */
export const MAX_UPLOAD_ROWS = 200_000;

/**
 * The free tier additionally caps a static import at 1,000 contacts.
 *
 * Declared here so the number lives in one place, but it can only be *applied*
 * where a tenant's tier is knowable — and no plan/entitlement model exists yet
 * (that arrives with admin ops). Until then this constant is the policy, and the
 * `row_limit_exceeded` rejection reason is how it would be reported.
 *
 * Rows beyond the cap are rejected **with a reason**, never silently truncated:
 * an import that quietly keeps the first N rows and says nothing is how a
 * customer discovers half their audience is missing after the campaign has run.
 */
export const FREE_TIER_MAX_IMPORT_ROWS = 3_000;

/** A sheet wider than this is almost certainly not a contact list. */
export const MAX_DETECTED_COLUMNS = 256;

/** How many data rows the detector reads before inferring types. */
export const DETECTION_SAMPLE_ROWS = 50;

/** Example values kept per detected column, for the mapping UI. */
export const DETECTED_COLUMN_SAMPLE_LIMIT = 3;

/** Rejected rows listed individually in the report; the rest are counted only. */
export const REJECTED_ROW_SAMPLE_LIMIT = 200;

/** Offending cell values are truncated to this before being reported back. */
export const REJECTED_VALUE_MAX_LENGTH = 200;

export const DATASET_FILE_TYPE_VALUES = ['xlsx', 'csv'] as const;
export const datasetFileTypeSchema = z.enum(DATASET_FILE_TYPE_VALUES);
export type DatasetFileType = z.infer<typeof datasetFileTypeSchema>;

/** Extensions the Dropzone advertises and the API accepts. */
export const ACCEPTED_UPLOAD_EXTENSIONS = ['.xlsx', '.csv'] as const;

/**
 * Content types browsers actually send for these files. The extension is what
 * decides the parser — this list is only a first-pass filter, because a real
 * XLSX regularly arrives as `application/octet-stream`.
 */
export const ACCEPTED_UPLOAD_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
  'application/csv',
  'text/plain',
  'application/octet-stream',
] as const;

/** Which parser a file needs, or null when the extension is not supported. */
export function datasetFileTypeFromFilename(filename: string): DatasetFileType | null {
  const lower = filename.trim().toLowerCase();
  if (lower.endsWith('.xlsx')) return 'xlsx';
  if (lower.endsWith('.csv')) return 'csv';
  return null;
}

/* -------------------------------------------------------------------------- */
/* Detected columns                                                            */
/* -------------------------------------------------------------------------- */

/**
 * The type the detector inferred for a column.
 *
 * This is deliberately richer than the three fields slice 1 can map onto: the
 * whole set is persisted in `datasets.detected_columns` because a later slice's
 * segment rule builder needs to know that "Signup date" is a date and "Order
 * count" is a number in order to offer the right operators.
 */
export const COLUMN_TYPE_VALUES = [
  'text',
  'number',
  'boolean',
  'date',
  'email',
  'phone',
] as const;
export const columnTypeSchema = z.enum(COLUMN_TYPE_VALUES);
export type ColumnType = z.infer<typeof columnTypeSchema>;

/** Zero-based position of a column in the source sheet. */
export const columnIndexSchema = z.number().int().min(0).max(MAX_DETECTED_COLUMNS - 1);

export const detectedColumnSchema = z.object({
  /** Zero-based position in the sheet. This, not `name`, is a column's identity. */
  index: columnIndexSchema,
  /**
   * Display name and `extras` key. Derived from the header cell and guaranteed
   * unique within a dataset: a blank header becomes `column_<n>` and a repeated
   * one is suffixed, because real spreadsheets contain both.
   */
  name: z.string().min(1).max(200),
  type: columnTypeSchema,
  /** Up to {@link DETECTED_COLUMN_SAMPLE_LIMIT} non-empty values, as text. */
  sampleValues: z.array(z.string().max(200)).max(DETECTED_COLUMN_SAMPLE_LIMIT),
  /**
   * 0–1: the share of sampled non-empty cells that matched `type`. The mapping
   * UI uses it to decide how loudly to suggest a mapping.
   */
  confidence: z.number().min(0).max(1),
});
export type DetectedColumn = z.infer<typeof detectedColumnSchema>;

export const detectedColumnsSchema = z.array(detectedColumnSchema).max(MAX_DETECTED_COLUMNS);

/* -------------------------------------------------------------------------- */
/* Column mapping                                                              */
/* -------------------------------------------------------------------------- */

/** The three contact fields a source column can be mapped onto. */
export const CONTACT_FIELD_VALUES = ['name', 'email', 'phone'] as const;
export const contactFieldSchema = z.enum(CONTACT_FIELD_VALUES);
export type ContactField = z.infer<typeof contactFieldSchema>;

/**
 * Which source column feeds each contact field, by zero-based column index.
 *
 * Everything absent from this object goes to `contacts.extras` under the
 * detected column's `name` — that is the whole reason the mapping is a small
 * allow-list rather than a per-column instruction: unmapped is not "ignored",
 * it is "kept".
 *
 * This is the *read* shape, used for a mapping that is already stored and was
 * validated when it was written. Validate an incoming one with
 * {@link columnMappingSchema}, which adds the two rules below.
 */
export const storedColumnMappingSchema = z.object({
  name: columnIndexSchema.optional(),
  email: columnIndexSchema.optional(),
  phone: columnIndexSchema.optional(),
});
export type StoredColumnMapping = z.infer<typeof storedColumnMappingSchema>;

/**
 * The same shape with the two rules that make a mapping importable:
 * a contact must be reachable, and one source column cannot feed two fields.
 * The database enforces the first independently, with `contacts_reachable`.
 */
export const columnMappingSchema = storedColumnMappingSchema
  .refine(
    (mapping) => mapping.email !== undefined || mapping.phone !== undefined,
    'Map at least one of email or phone — a contact with neither can never be reached',
  )
  .refine((mapping) => {
    const used = [mapping.name, mapping.email, mapping.phone].filter(
      (index): index is number => index !== undefined,
    );
    return new Set(used).size === used.length;
  }, 'Each source column can be mapped to only one field');
export type ColumnMapping = z.infer<typeof columnMappingSchema>;

/** Confirming the mapping is what starts the import. */
export const setColumnMappingRequestSchema = z.object({
  mapping: columnMappingSchema,
});
export type SetColumnMappingRequest = z.infer<typeof setColumnMappingRequestSchema>;

/** Column indexes that will land in `extras` under the given mapping. */
export function unmappedColumns(
  columns: DetectedColumn[],
  mapping: StoredColumnMapping,
): DetectedColumn[] {
  const mapped = new Set(
    [mapping.name, mapping.email, mapping.phone].filter(
      (index): index is number => index !== undefined,
    ),
  );
  return columns.filter((column) => !mapped.has(column.index));
}

/* -------------------------------------------------------------------------- */
/* Rejected rows                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Why a source row did not become a contact. Every rejected row carries one of
 * these — "nothing silently dropped" is a slice-1 acceptance criterion.
 */
export const REJECTED_ROW_REASON_VALUES = [
  /** Every mapped and unmapped cell in the row was blank. */
  'empty_row',
  /** Neither an email nor a phone survived normalisation. */
  'missing_contact_method',
  /** The mapped email cell is not a valid address. */
  'invalid_email',
  /** The mapped phone cell could not be normalised to E.164. */
  'invalid_phone',
  /** The row is past {@link MAX_UPLOAD_ROWS} or the tenant's tier cap. */
  'row_limit_exceeded',
] as const;
export const rejectedRowReasonSchema = z.enum(REJECTED_ROW_REASON_VALUES);
export type RejectedRowReason = z.infer<typeof rejectedRowReasonSchema>;

export const rejectedRowSchema = z.object({
  /**
   * 1-based row number in the source sheet, counting the header, so it matches
   * what the user sees when they open the file to fix it.
   */
  row: z.number().int().min(1),
  reason: rejectedRowReasonSchema,
  /** Human-readable explanation, safe to render as-is. */
  message: z.string().max(300),
  /** The column the problem came from, when the reason is column-specific. */
  column: z.string().max(200).optional(),
  /** The offending value, truncated to {@link REJECTED_VALUE_MAX_LENGTH}. */
  value: z.string().max(REJECTED_VALUE_MAX_LENGTH).optional(),
});
export type RejectedRow = z.infer<typeof rejectedRowSchema>;

/**
 * The report shown after an import.
 *
 * `rows` is capped at {@link REJECTED_ROW_SAMPLE_LIMIT} — a 200k-row file can
 * reject more than anyone will read — so `total` and `byReason` are what tell
 * the whole story, and `truncated` says the list is a sample.
 */
export const rejectedRowReportSchema = z.object({
  datasetId: uuidSchema,
  /** Every rejected row, including those not listed in `rows`. */
  total: z.number().int().min(0),
  byReason: z.record(rejectedRowReasonSchema, z.number().int().min(0)),
  rows: z.array(rejectedRowSchema).max(REJECTED_ROW_SAMPLE_LIMIT),
  truncated: z.boolean(),
});
export type RejectedRowReport = z.infer<typeof rejectedRowReportSchema>;

/* -------------------------------------------------------------------------- */
/* Read                                                                        */
/* -------------------------------------------------------------------------- */

export const datasetSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  /** The uploaded file's own name, kept for display. */
  sourceFilename: z.string(),
  sourceBytes: z.number().int().min(0),
  /**
   * Which worksheet of the source file this dataset reads, zero-based, and its
   * name. A multi-sheet workbook yields one dataset per sheet, so the UI has to
   * be able to say which tab a dataset came from. Null name / 0 index for CSV.
   */
  sheetIndex: z.number().int().min(0),
  sheetName: z.string().nullable(),
  /** Data rows found in the file, header excluded. 0 until parsing finishes. */
  rowCount: z.number().int().min(0),
  importedCount: z.number().int().min(0),
  rejectedCount: z.number().int().min(0),
  status: datasetStatusSchema,
  /** Why the import failed. Non-null exactly when `status` is `failed`. */
  error: z.string().nullable(),
  detectedColumns: detectedColumnsSchema,
  /** Null until the user confirms a mapping. */
  columnMapping: storedColumnMappingSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type Dataset = z.infer<typeof datasetSchema>;

export const datasetResponseSchema = z.object({ dataset: datasetSchema });
export type DatasetResponse = z.infer<typeof datasetResponseSchema>;

/** Row shape for the dataset list. Detected columns are left out on purpose. */
export const datasetSummarySchema = z.object({
  id: uuidSchema,
  name: z.string(),
  sourceFilename: z.string(),
  /**
   * Carried on the summary, not just the full dataset, because a list of two
   * datasets from one workbook is otherwise ambiguous — both show the same
   * source file name and nothing says which tab each came from.
   */
  sheetName: z.string().nullable(),
  status: datasetStatusSchema,
  rowCount: z.number().int().min(0),
  importedCount: z.number().int().min(0),
  rejectedCount: z.number().int().min(0),
  createdAt: isoDateTimeSchema,
});
export type DatasetSummary = z.infer<typeof datasetSummarySchema>;

export const DATASET_SORT_VALUES = ['createdAt', 'name'] as const;
export const datasetSortSchema = z.enum(DATASET_SORT_VALUES);
export type DatasetSort = z.infer<typeof datasetSortSchema>;

export const datasetListQuerySchema = paginationQuerySchema.extend({
  /** Matches the dataset name or the source file name, case-insensitive. */
  search: z.string().trim().max(120).optional(),
  sort: datasetSortSchema.default('createdAt'),
  direction: sortDirectionSchema.default('desc'),
});
export type DatasetListQuery = z.infer<typeof datasetListQuerySchema>;

export const datasetListResponseSchema = paginated(datasetSummarySchema);
export type DatasetListResponse = z.infer<typeof datasetListResponseSchema>;

export const datasetParamsSchema = z.object({ datasetId: uuidSchema });
export type DatasetParams = z.infer<typeof datasetParamsSchema>;

/* -------------------------------------------------------------------------- */
/* Upload — step 1                                                             */
/* -------------------------------------------------------------------------- */

/**
 * The non-file part of the multipart upload. The file itself is validated by
 * the API against {@link MAX_UPLOAD_BYTES} and
 * {@link datasetFileTypeFromFilename}, not by a zod schema.
 */
export const uploadDatasetRequestSchema = z.object({
  /** Defaults to the file name without its extension. */
  name: z.string().trim().min(1).max(120).optional(),
});
export type UploadDatasetRequest = z.infer<typeof uploadDatasetRequestSchema>;

/**
 * The answer to an upload: the pending dataset, its detected columns, and the
 * mapping we think is right. The suggestion is a starting point for the user to
 * correct — it is never applied on its own, which is why it travels beside the
 * dataset rather than being written to `columnMapping`.
 *
 * Confirming a mapping (`setColumnMappingRequestSchema`) answers with the plain
 * {@link datasetResponseSchema}, by then in `processing`.
 */
/**
 * One entry per dataset created by an upload.
 *
 * A workbook with several sheets produces **one dataset per sheet**, not one
 * dataset from the first sheet. Real exports carry unrelated audiences on
 * separate tabs — an email list on one, a phone list on another, of different
 * lengths and with no key joining them. Merging them would pair one person's
 * email with a different person's phone number, so each sheet stays its own
 * dataset and they are combined deliberately, as a segment.
 */
export const uploadedSheetSchema = z.object({
  dataset: datasetSchema,
  suggestedMapping: storedColumnMappingSchema,
  /** Sheet name as it appears in the workbook. Null for CSV. */
  sheetName: z.string().nullable(),
  /** Zero-based position in the workbook. 0 for CSV. */
  sheetIndex: z.number().int().min(0),
});
export type UploadedSheet = z.infer<typeof uploadedSheetSchema>;

export const uploadDatasetResponseSchema = z.object({
  /** One per sheet, in workbook order. Always at least one entry. */
  sheets: z.array(uploadedSheetSchema).min(1),
  /**
   * Sheets found in the workbook but skipped because they held no usable rows.
   * Reported rather than ignored, so an empty tab is visibly a non-event
   * instead of looking like data that went missing.
   */
  skippedSheets: z.array(z.object({ name: z.string(), index: z.number().int().min(0), reason: z.string() })),
});
export type UploadDatasetResponse = z.infer<typeof uploadDatasetResponseSchema>;

/* -------------------------------------------------------------------------- */
/* Status — step 3                                                             */
/* -------------------------------------------------------------------------- */

/**
 * The cheap poll target while an import runs. Deliberately smaller than the
 * full dataset: the UI hits it on a timer until `status` leaves `processing`.
 */
export const datasetStatusResponseSchema = z.object({
  id: uuidSchema,
  status: datasetStatusSchema,
  rowCount: z.number().int().min(0),
  importedCount: z.number().int().min(0),
  rejectedCount: z.number().int().min(0),
  error: z.string().nullable(),
});
export type DatasetStatusResponse = z.infer<typeof datasetStatusResponseSchema>;

/** True while the dataset is still being worked on and the UI should keep polling. */
export function isDatasetInFlight(status: DatasetStatusResponse['status']): boolean {
  return status === 'pending' || status === 'processing';
}
